<template>
    <div class="tour-media-upload">
        <div class="upload-tabs">
            <button 
                v-for="tab in tabs" 
                :key="tab.value"
                :class="['upload-tab', { active: activeTab === tab.value }]"
                @click="activeTab = tab.value"
            >
                <theme-icon :icon="tab.icon" type="icons" />
                <span>{{ tab.label }}</span>
            </button>
        </div>

        <div class="upload-content">
            <!-- Photo Upload -->
            <div v-if="activeTab === 'photo'" class="upload-section">
                <div 
                    class="dropzone"
                    :class="{ 'dragover': isDragging }"
                    @drop.prevent="handlePhotoDrop"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                >
                    <theme-icon icon="camera" type="icons" />
                    <p>Drag & drop photos here or click to browse</p>
                    <input 
                        ref="photoInput"
                        type="file" 
                        accept="image/*"
                        multiple
                        @change="handlePhotoSelect"
                        style="display: none"
                    />
                    <themed-button @click="$refs.photoInput.click()">
                        Choose Photos
                    </themed-button>
                </div>

                <div v-if="selectedPhotos.length" class="preview-grid">
                    <div v-for="(photo, index) in selectedPhotos" :key="index" class="preview-item">
                        <img :src="photo.preview" :alt="photo.file.name" />
                        <div class="preview-overlay">
                            <button @click="removePhoto(index)" class="remove-btn">
                                <theme-icon icon="close" type="icons" />
                            </button>
                        </div>
                        <themed-input
                            v-model="photo.caption"
                            placeholder="Add caption..."
                            class="caption-input"
                        />
                    </div>
                </div>
            </div>

            <!-- Video Upload -->
            <div v-if="activeTab === 'video'" class="upload-section">
                <div class="dropzone">
                    <theme-icon icon="video" type="icons" />
                    <p>Select a video to upload (max 50MB)</p>
                    <input 
                        ref="videoInput"
                        type="file" 
                        accept="video/*"
                        @change="handleVideoSelect"
                        style="display: none"
                    />
                    <themed-button @click="$refs.videoInput.click()">
                        Choose Video
                    </themed-button>
                </div>

                <div v-if="selectedVideo" class="preview-single">
                    <div class="video-preview">
                        <theme-icon icon="video" type="icons" />
                        <span>{{ selectedVideo.file.name }}</span>
                        <span class="file-size">{{ formatFileSize(selectedVideo.file.size) }}</span>
                        <button @click="selectedVideo = null" class="remove-btn">
                            <theme-icon icon="close" type="icons" />
                        </button>
                    </div>
                    <themed-input
                        v-model="selectedVideo.caption"
                        placeholder="Add caption..."
                    />
                </div>
            </div>

            <!-- Note Editor -->
            <div v-if="activeTab === 'note'" class="upload-section">
                <themed-input
                    v-model="noteTitle"
                    placeholder="Note title (optional)"
                    class="note-title"
                />
                <div class="note-editor">
                    <textarea
                        v-model="noteContent"
                        placeholder="Write your notes here..."
                        rows="10"
                    ></textarea>
                </div>
            </div>
        </div>

        <div class="upload-footer">
            <div class="comp-selector">
                <label>Associate with property:</label>
                <themed-select
                    v-model="selectedCompId"
                    :options="compOptions"
                    placeholder="Select property (optional)"
                />
            </div>

            <div class="upload-actions">
                <themed-button variant="secondary" @click="$emit('close')">
                    Cancel
                </themed-button>
                <themed-button 
                    @click="handleUpload" 
                    :disabled="!canUpload || isUploading"
                >
                    <span v-if="isUploading">Uploading...</span>
                    <span v-else>Upload</span>
                </themed-button>
            </div>
        </div>

        <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <span>{{ uploadProgress }}%</span>
        </div>
    </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
    name: 'TourMediaUpload',

    components: {
        ThemedButton,
        ThemedInput,
        ThemedSelect,
        ThemeIcon,
    },

    props: {
        tourId: {
            type: Number,
            required: true,
        },
        comps: {
            type: Array,
            default: () => [],
        },
        defaultCompId: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            activeTab: 'photo',
            tabs: [
                { value: 'photo', label: 'Photos', icon: 'camera' },
                { value: 'video', label: 'Video', icon: 'video' },
                { value: 'note', label: 'Notes', icon: 'note' },
            ],
            selectedPhotos: [],
            selectedVideo: null,
            noteTitle: '',
            noteContent: '',
            selectedCompId: this.defaultCompId,
            isDragging: false,
            isUploading: false,
            uploadProgress: 0,
        };
    },

    computed: {
        compOptions() {
            return [
                { value: null, label: 'General (not specific to property)' },
                ...this.comps.map(comp => ({
                    value: comp.id,
                    label: comp.comp?.building?.name || comp.building?.name || 'Unknown Property',
                })),
            ];
        },

        canUpload() {
            if (this.activeTab === 'photo') {
                return this.selectedPhotos.length > 0;
            }
            if (this.activeTab === 'video') {
                return this.selectedVideo !== null;
            }
            if (this.activeTab === 'note') {
                return this.noteContent.trim().length > 0;
            }
            return false;
        },
    },

    methods: {
        handlePhotoSelect(event) {
            const files = Array.from(event.target.files);
            this.addPhotos(files);
        },

        handlePhotoDrop(event) {
            this.isDragging = false;
            const files = Array.from(event.dataTransfer.files).filter(file => 
                file.type.startsWith('image/')
            );
            this.addPhotos(files);
        },

        addPhotos(files) {
            files.forEach(file => {
                if (file.size > 10 * 1024 * 1024) {
                    this.$notify({
                        type: 'error',
                        title: 'File Too Large',
                        text: `${file.name} exceeds 10MB limit`,
                    });
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    this.selectedPhotos.push({
                        file,
                        preview: e.target.result,
                        caption: '',
                    });
                };
                reader.readAsDataURL(file);
            });
        },

        removePhoto(index) {
            this.selectedPhotos.splice(index, 1);
        },

        handleVideoSelect(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > 50 * 1024 * 1024) {
                this.$notify({
                    type: 'error',
                    title: 'File Too Large',
                    text: 'Video must be under 50MB',
                });
                return;
            }

            this.selectedVideo = {
                file,
                caption: '',
            };
        },

        async handleUpload() {
            if (!this.canUpload || this.isUploading) return;

            this.isUploading = true;
            this.uploadProgress = 0;

            try {
                if (this.activeTab === 'photo') {
                    await this.uploadPhotos();
                } else if (this.activeTab === 'video') {
                    await this.uploadVideo();
                } else if (this.activeTab === 'note') {
                    await this.saveNote();
                }

                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: 'Media uploaded successfully',
                });

                this.$emit('uploaded');
                this.resetForm();
            } catch (error) {
                console.error('Upload error:', error);
                this.$notify({
                    type: 'error',
                    title: 'Upload Failed',
                    text: error.response?.data?.message || 'Failed to upload media',
                });
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
            }
        },

        async uploadPhotos() {
            const total = this.selectedPhotos.length;
            
            for (let i = 0; i < total; i++) {
                const photo = this.selectedPhotos[i];
                const formData = new FormData();
                formData.append('photo', photo.file);
                formData.append('caption', photo.caption);
                if (this.selectedCompId) {
                    formData.append('tour_comp_id', this.selectedCompId);
                }

                await axios.post(`/tours/${this.tourId}/media/photo`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                this.uploadProgress = Math.round(((i + 1) / total) * 100);
            }
        },

        async uploadVideo() {
            const formData = new FormData();
            formData.append('video', this.selectedVideo.file);
            formData.append('caption', this.selectedVideo.caption);
            if (this.selectedCompId) {
                formData.append('tour_comp_id', this.selectedCompId);
            }

            await axios.post(`/tours/${this.tourId}/media/video`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    this.uploadProgress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                },
            });
        },

        async saveNote() {
            await axios.post(`/tours/${this.tourId}/media/note`, {
                content: this.noteContent,
                caption: this.noteTitle,
                tour_comp_id: this.selectedCompId,
            });
            this.uploadProgress = 100;
        },

        resetForm() {
            this.selectedPhotos = [];
            this.selectedVideo = null;
            this.noteTitle = '';
            this.noteContent = '';
            this.selectedCompId = this.defaultCompId;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },
    },
};
</script>
